import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Download, Eye, ChevronLeft, ChevronRight } from "lucide-react";
import { useAuth } from "../../context/authContext";
import { ROLES } from "../../constants/roles";
import { useAdminVenturesHook } from "../../hooks/useAdminHooks";
import { useManagerVenturesHook } from "../../hooks/useManagerHooks";

import {
  Card,
  PageHeader,
  Badge,
  STAGE_BADGE_TONES,
} from "../adminComponents/adminComponents";
import {
  STAGE_LABELS,
  SECTOR_LABELS,
  GENDER_LABELS,
} from "../../data/mockData";
import "./ventures.css";

function normalizeStage(level) {
  const normalized = String(level ?? "")
    .trim()
    .toLowerCase();
  return STAGE_LABELS[normalized] ? normalized : null;
}

function normalizeSector(sector) {
  const normalized = String(sector ?? "")
    .trim()
    .toLowerCase();
  return normalized || null;
}

function getSectorLabel(sector) {
  const normalized = normalizeSector(sector);
  if (!normalized) {
    return "—";
  }

  const label = SECTOR_LABELS[normalized];
  if (label) {
    return label;
  }

  return String(sector).trim() || "—";
}

function parseCohortParts(cohort) {
  const value = String(cohort ?? "").toLowerCase();
  const year = value.match(/\b(20\d{2})\b/)?.[1] ?? null;
  const quarter = value.match(/\bq([1-4])\b/)?.[0] ?? null;
  return { year, quarter };
}

function mapVentureForUi(venture) {
  const stage = normalizeStage(venture.level);
  const sector = normalizeSector(venture.sector);
  const founderGender = String(venture.founder?.gender ?? "")
    .trim()
    .toLowerCase();
  const genderClassification =
    String(venture.gender_classification ?? "").trim() ||
    (founderGender && founderGender !== "male" ? "women_led" : null);
  const cohort = venture.cohort ?? "—";
  const { year: cohortYear, quarter: cohortQuarter } = parseCohortParts(cohort);

  return {
    id: venture.id,
    name: venture.name,
    founderName: venture.founder?.name ?? "—",
    founderEmail: venture.founder?.email ?? null,
    sector,
    sectorRaw: venture.sector ?? "—",
    geography: venture.geography ?? "—",
    cohort,
    cohortYear,
    cohortQuarter,
    stage,
    stageRaw: venture.level ?? "—",
    teamSize: venture.team_size ?? null,
    genderClassification,
  };
}

export default function Ventures() {
  const { user, token } = useAuth();
  const isAdmin = user?.role === ROLES.ADMIN;
  const isManager = user?.role === ROLES.MANAGER;
  const workspaceBase = user?.role === ROLES.ADMIN ? "/admin" : "/manager";
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [sectorFilter, setSectorFilter] = useState("all");
  const [cohortYearFilter, setCohortYearFilter] = useState("all");
  const [cohortQuarterFilter, setCohortQuarterFilter] = useState("all");
  const [rowsPerPage, setRowsPerPage] = useState("10");
  const [currentPage, setCurrentPage] = useState(1);
  const [isMobilePagination, setIsMobilePagination] = useState(false);

  const adminVenturesQuery = useAdminVenturesHook({
    token,
    userId: user?.id,
    enabled: isAdmin,
  });

  const managerVenturesQuery = useManagerVenturesHook({
    token,
    userId: user?.id,
    enabled: isManager,
  });

  const venturesQuery = isManager ? managerVenturesQuery : adminVenturesQuery;

  const ventures = (venturesQuery.data ?? []).map(mapVentureForUi);
  const sectorOptions = Array.from(
    new Map(
      ventures
        .map((venture) => {
          if (!venture.sector) {
            return null;
          }

          return [venture.sector, getSectorLabel(venture.sectorRaw)];
        })
        .filter(Boolean),
    ),
  )
    .map(([value, label]) => ({ value, label }))
    .sort((left, right) => left.label.localeCompare(right.label));
  const cohortYearOptions = Array.from(
    new Set(
      ventures
        .map((venture) => venture.cohortYear)
        .filter((year) => Boolean(year)),
    ),
  ).sort((left, right) => right.localeCompare(left));
  const cohortQuarterOptions = ["q1", "q2", "q3", "q4"];

  const filtered = ventures.filter((venture) => {
    const query = search.toLowerCase();
    const matchesSearch =
      venture.name.toLowerCase().includes(query) ||
      venture.id.toLowerCase().includes(query) ||
      venture.founderName.toLowerCase().includes(query) ||
      venture.founderEmail?.toLowerCase().includes(query) ||
      venture.sectorRaw.toLowerCase().includes(query) ||
      venture.geography.toLowerCase().includes(query) ||
      venture.cohort.toLowerCase().includes(query) ||
      venture.stageRaw.toLowerCase().includes(query) ||
      venture.teamSize?.toString().toLowerCase().includes(query) ||
      venture.genderClassification?.toLowerCase().includes(query);
    const matchesStage = stageFilter === "all" || venture.stage === stageFilter;
    const matchesSector =
      sectorFilter === "all" || venture.sector === sectorFilter;
    const matchesCohortYear =
      cohortYearFilter === "all" || venture.cohortYear === cohortYearFilter;
    const matchesCohortQuarter =
      cohortQuarterFilter === "all" ||
      venture.cohortQuarter === cohortQuarterFilter;
    return (
      matchesSearch &&
      matchesStage &&
      matchesSector &&
      matchesCohortYear &&
      matchesCohortQuarter
    );
  });

  const clearFilters = () => {
    setSearch("");
    setStageFilter("all");
    setSectorFilter("all");
    setCohortYearFilter("all");
    setCohortQuarterFilter("all");
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [
    search,
    stageFilter,
    sectorFilter,
    cohortYearFilter,
    cohortQuarterFilter,
    rowsPerPage,
  ]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 640px)");
    const applyMatch = (event) => {
      setIsMobilePagination(event.matches);
    };

    setIsMobilePagination(mediaQuery.matches);
    mediaQuery.addEventListener("change", applyMatch);
    return () => mediaQuery.removeEventListener("change", applyMatch);
  }, []);

  const totalFiltered = filtered.length;

  useEffect(() => {
    if (totalFiltered > 0 && totalFiltered < 10) {
      setRowsPerPage(String(totalFiltered));
    } else if (totalFiltered >= 10 && Number(rowsPerPage) < 10) {
      setRowsPerPage("10");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalFiltered]);

  const pageSize = Number(rowsPerPage);
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * pageSize;
  const endIndex = Math.min(totalFiltered, startIndex + pageSize);
  const paginatedVentures = filtered.slice(startIndex, startIndex + pageSize);

  const buildPageItems = () => {
    if (isMobilePagination) {
      if (totalPages <= 4) {
        return Array.from({ length: totalPages }, (_, index) => index + 1);
      }

      const compact = [1, safeCurrentPage, totalPages]
        .filter((page, index, pages) => pages.indexOf(page) === index)
        .sort((a, b) => a - b);
      const items = [];

      for (let index = 0; index < compact.length; index += 1) {
        const page = compact[index];
        const prev = compact[index - 1];
        if (index > 0 && page - prev > 1) {
          items.push("ellipsis");
        }
        items.push(page);
      }

      return items;
    }

    if (totalPages <= 6) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const first = [1, 2];
    const last = [totalPages - 1, totalPages];
    const middle =
      safeCurrentPage > 2 && safeCurrentPage < totalPages - 1
        ? [safeCurrentPage]
        : [];
    const merged = [...first, ...middle, ...last]
      .filter((page, index, pages) => pages.indexOf(page) === index)
      .sort((a, b) => a - b);

    const items = [];
    for (let index = 0; index < merged.length; index += 1) {
      const page = merged[index];
      const prev = merged[index - 1];
      if (index > 0 && page - prev > 1) {
        items.push("ellipsis");
      }
      items.push(page);
    }
    return items;
  };

  const pageItems = buildPageItems();
  const hasActiveFilters =
    search.trim() !== "" ||
    stageFilter !== "all" ||
    sectorFilter !== "all" ||
    cohortYearFilter !== "all" ||
    cohortQuarterFilter !== "all";
  const showLoadingState = venturesQuery.isLoading;
  const showErrorState = venturesQuery.isError;
  const showTableState = !showLoadingState && !showErrorState;
  const showEmptyState = showTableState && filtered.length === 0;
  const showUnfilteredEmptyState = showEmptyState && ventures.length === 0;

  const handleExportCsv = () => {
    const rowsToExport = filtered;
    const headers = [
      "Venture",
      "Founder",
      "Founder Email",
      "Sector",
      "Geography",
      "Cohort",
      "Stage",
      "Team Size",
      "Gender Classification",
    ];

    const escapeCsv = (value) => {
      const serialized = String(value ?? "");
      if (/[",\n]/.test(serialized)) {
        return `"${serialized.replace(/"/g, '""')}"`;
      }
      return serialized;
    };

    const csvRows = [
      headers.join(","),
      ...rowsToExport.map((venture) =>
        [
          venture.name,
          venture.founderName,
          venture.founderEmail ?? "",
          SECTOR_LABELS[venture.sector] ?? venture.sectorRaw,
          venture.geography,
          venture.cohort,
          STAGE_LABELS[venture.stage] ?? venture.stageRaw,
          venture.teamSize ?? "",
          GENDER_LABELS[venture.genderClassification] ??
            venture.genderClassification ??
            "",
        ]
          .map(escapeCsv)
          .join(","),
      ),
    ];

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const isDefaultFilterSet =
      search.trim() === "" &&
      stageFilter === "all" &&
      sectorFilter === "all" &&
      cohortYearFilter === "all" &&
      cohortQuarterFilter === "all";
    const dateStamp = new Date().toISOString().slice(0, 10);

    link.href = url;
    link.download = isDefaultFilterSet
      ? `ventures-${dateStamp}.csv`
      : `ventures-filtered-${dateStamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="adminVentures-root">
      <PageHeader
        title="Venture Registry"
        description="Manage and track all ventures in your portfolio"
        actions={
          <>
            <button
              className="admin-btn admin-btn-outline"
              type="button"
              onClick={handleExportCsv}
            >
              <Download className="admin-btn-icon" />
              Export
            </button>
          </>
        }
      />

      {/* Filters */}
      <Card className="adminVentures-filters">
        <div className="adminVentures-filtersRow">
          <div className="admin-input-wrap">
            <Search className="admin-input-icon" aria-hidden="true" />
            <input
              type="search"
              className="admin-input admin-input-search"
              placeholder="Search ventures or founders..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <select
            className="admin-select adminVentures-filter"
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value)}
            aria-label="Stage filter"
          >
            <option value="all">All Stages</option>
            {Object.entries(STAGE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>

          <select
            className="admin-select adminVentures-filter"
            value={sectorFilter}
            onChange={(e) => setSectorFilter(e.target.value)}
            aria-label="Sector filter"
          >
            <option value="all">All Sectors</option>
            {sectorOptions.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>

          <select
            className="admin-select adminVentures-filter"
            value={cohortYearFilter}
            onChange={(e) => setCohortYearFilter(e.target.value)}
            aria-label="Cohort year filter"
          >
            <option value="all">All Years</option>
            {cohortYearOptions.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>

          <select
            className="admin-select adminVentures-filter"
            value={cohortQuarterFilter}
            onChange={(e) => setCohortQuarterFilter(e.target.value)}
            aria-label="Cohort quarter filter"
          >
            <option value="all">All Quarters</option>
            {cohortQuarterOptions.map((value) => (
              <option key={value} value={value}>
                {value.toUpperCase()}
              </option>
            ))}
          </select>
        </div>
      </Card>

      {/* Table */}
      <Card>
        {showLoadingState && (
          <div className="adminVentures-empty">
            <p className="admin-muted">Loading ventures...</p>
          </div>
        )}

        {showErrorState && (
          <div className="adminVentures-empty">
            <p className="admin-muted">
              {venturesQuery.error?.message || "Unable to load ventures"}
            </p>
          </div>
        )}

        {showTableState && (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Venture</th>
                  <th>Founder</th>
                  <th>Sector</th>
                  <th>Geography</th>
                  <th>Cohort</th>
                  <th>Stage</th>
                  <th>Team Size</th>
                  <th className="adminVentures-actionsCol">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedVentures.map((venture, rowIndex) => (
                  <tr
                    key={`${venture.id ?? "no-id"}-${venture.name ?? "no-name"}-${venture.cohort ?? "no-cohort"}-${startIndex + rowIndex}`}
                  >
                    <td>
                      <div className="adminVentures-cellStack">
                        <span className="adminVentures-name">
                          {venture.name}
                        </span>
                        <span className="admin-muted adminVentures-meta">
                          {venture.id ?? "—"}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="adminVentures-cellStack">
                        <span>{venture.founderName}</span>
                        <span className="admin-muted adminVentures-meta">
                          {venture.founderEmail ?? "—"}
                        </span>
                      </div>
                    </td>
                    <td>{getSectorLabel(venture.sectorRaw)}</td>
                    <td>{venture.geography}</td>
                    <td>{venture.cohort}</td>
                    <td>
                      <Badge tone={STAGE_BADGE_TONES[venture.stage] ?? "muted"}>
                        {STAGE_LABELS[venture.stage] ?? venture.stageRaw}
                      </Badge>
                    </td>
                    <td>{venture.teamSize ?? "—"}</td>
                    <td className="adminVentures-actionsCol">
                      <Link
                        to={`${workspaceBase}/ventures/${encodeURIComponent(
                          venture.id,
                        )}`}
                        className="admin-btn admin-btn-ghost admin-btn-sm"
                        aria-label={`View ${venture.name}`}
                      >
                        <Eye className="admin-btn-icon" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {showEmptyState && (
              <div className="adminVentures-empty">
                <p className="admin-muted">
                  {showUnfilteredEmptyState
                    ? "No ventures available yet"
                    : "No ventures found matching your filters"}
                </p>
                {hasActiveFilters && (
                  <button
                    type="button"
                    className="admin-btn admin-btn-ghost admin-btn-sm"
                    onClick={clearFilters}
                  >
                    Clear all filters
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </Card>

      {showTableState && (
        <div className="adminVentures-pagination">
          <div className="adminVentures-pageSizeWrap">
            <label
              className="adminVentures-pageSizeLabel"
              htmlFor="ventures-rows-per-page"
            >
              Showing
            </label>
            <select
              id="ventures-rows-per-page"
              className="admin-select adminVentures-pageSize"
              value={rowsPerPage}
              onChange={(event) => setRowsPerPage(event.target.value)}
              aria-label="Rows per page"
            >
              {totalFiltered > 0 && totalFiltered < 10 && (
                <option value={String(totalFiltered)}>{totalFiltered}</option>
              )}
              <option value="10">10</option>
              <option value="30">30</option>
              <option value="50">50</option>
            </select>
            <p className="admin-muted adminVentures-count">
              of {totalFiltered} ventures
            </p>
          </div>
          <div className="adminVentures-paginationControls">
            <button
              type="button"
              className="adminVentures-pageButton"
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              disabled={safeCurrentPage <= 1}
              aria-label="Previous page"
            >
              <ChevronLeft size={16} />
            </button>

            {pageItems.map((item, index) =>
              item === "ellipsis" ? (
                <span
                  key={`ellipsis-${index}`}
                  className="adminVentures-pageEllipsis"
                >
                  ...
                </span>
              ) : (
                <button
                  key={item}
                  type="button"
                  className={
                    "adminVentures-pageButton" +
                    (safeCurrentPage === item
                      ? " adminVentures-pageButton-active"
                      : "")
                  }
                  onClick={() => setCurrentPage(item)}
                >
                  {item}
                </button>
              ),
            )}

            <button
              type="button"
              className="adminVentures-pageButton"
              onClick={() =>
                setCurrentPage((page) => Math.min(totalPages, page + 1))
              }
              disabled={safeCurrentPage >= totalPages}
              aria-label="Next page"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
